import classNames from "classnames";
import { Check, Minus } from "lucide-react";
import type * as React from "react";

import type { OrgLlmConnectionRecord, OrgLlmModel } from "@/shared/domain/orgs";
import { isRobotoManaged } from "@/shared/domain/orgs/orgModelConfig";
import {
  Badge,
  Checkbox,
  Spinner,
  Table,
  Tooltip,
} from "@/shared/foundation/primitives";

import { isEnabled, modelSourceLabel } from "./modelList";
import { ModelStatus } from "./ModelVerification";
import styles from "./OrgAiModels.module.css";

/** The row standing for Roboto's utility model while no model on the list is the utility model. */
const ROBOTO_UTILITY_LABEL = "Roboto utility model";

/** What the utility model does, on its badge's tooltip. */
const UTILITY_ROLE = "Runs chat titles, compression and log scans.";

/** Why the default model's Enabled switch is locked on. */
const DEFAULT_STAYS_ON = "Make another model the default first";

/** What lets an admin switch models on and off from the table. */
export interface ModelEnabledControl {
  /** Saves the list with `model` switched on or off. */
  onToggle: (model: OrgLlmModel, enabled: boolean) => void;
  /**
   * A save is in flight. Every switch ignores clicks until it settles, so no
   * second save queues behind it, but keeps its normal look.
   */
  busy: boolean;
  /** The id of the model whose switch started the save in flight, shown as a spinner meanwhile. */
  pendingId?: string;
}

interface ModelTableProps {
  models: readonly OrgLlmModel[];
  connections: readonly OrgLlmConnectionRecord[];
  isPremium: boolean;
  /**
   * Whether the org may use its own LLM connections at all (a feature flag).
   * Off, the table shows no utility model: no Utility badge and no Roboto
   * utility row.
   */
  ownConnectionsEnabled: boolean;
  /**
   * Provider model ids of Roboto-managed models, by catalog id, when the
   * caller can see the catalog. A Roboto model without one shows no id.
   */
  robotoModelIds?: ReadonlyMap<string, string>;
  /**
   * The provider model id behind Roboto's utility model, when the caller can
   * see the catalog; the Roboto utility row shows none without it.
   */
  robotoUtilityModelId?: string;
  /** Makes the Enabled column editable; omitted, it only shows each model's state. */
  enabledControl?: ModelEnabledControl;
  /** A row's actions cell; omitted for a read-only table with no Actions column. */
  renderActions?: (model: OrgLlmModel) => React.ReactNode;
}

/**
 * The org's model list as a table: whether each model is switched on, the
 * model, where it runs, its provider model id, its roles and whether it can
 * run. A switched-off model stays on the list, dimmed. With own connections
 * enabled, when no model on the list is the utility model, a last row shows
 * Roboto's, which then runs titles, compression and log scans. Scrolls
 * sideways inside its own box on narrow screens.
 */
export function ModelTable({
  models,
  connections,
  isPremium,
  ownConnectionsEnabled,
  robotoModelIds,
  robotoUtilityModelId,
  enabledControl,
  renderActions,
}: ModelTableProps) {
  const robotoRunsUtility =
    ownConnectionsEnabled && !models.some((m) => m.is_utility === true);
  return (
    <div className={styles.tableScroll}>
      <Table aria-label="Models" aria-busy={enabledControl?.busy === true}>
        <Table.Header>
          <Table.Row>
            <Table.Head scope="col">Enabled</Table.Head>
            <Table.Head scope="col">Model</Table.Head>
            <Table.Head scope="col">Runs on</Table.Head>
            <Table.Head scope="col">Model id</Table.Head>
            <Table.Head scope="col">Role</Table.Head>
            <Table.Head scope="col">Status</Table.Head>
            {renderActions !== undefined && (
              <Table.Head scope="col">
                <span className="sr-only">Actions</span>
              </Table.Head>
            )}
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {models.map((model) => (
            <Table.Row
              key={model.id}
              aria-label={model.label}
              className={classNames(!isEnabled(model) && styles.rowOff)}
            >
              <Table.Cell>
                <EnabledSwitch model={model} control={enabledControl} />
              </Table.Cell>
              <Table.Cell className={styles.dimmable}>
                <span className={styles.modelCell}>
                  <span className={styles.modelLabel}>{model.label}</span>
                  {model.description !== "" && (
                    <span className={styles.secondaryText}>
                      {model.description}
                    </span>
                  )}
                </span>
              </Table.Cell>
              <Table.Cell
                className={classNames(styles.nowrap, styles.dimmable)}
              >
                {modelSourceLabel(model, connections)}
              </Table.Cell>
              <Table.Cell
                className={classNames(styles.modelId, styles.dimmable)}
              >
                {isRobotoManaged(model)
                  ? robotoModelIds?.get(model.id)
                  : model.model_id}
              </Table.Cell>
              <Table.Cell className={styles.dimmable}>
                <ModelRoles model={model} showUtility={ownConnectionsEnabled} />
              </Table.Cell>
              <Table.Cell className={styles.dimmable}>
                <ModelStatus
                  model={model}
                  connections={connections}
                  isPremium={isPremium}
                  ownConnectionsEnabled={ownConnectionsEnabled}
                />
              </Table.Cell>
              {renderActions !== undefined && (
                <Table.Cell className={styles.actionsCell}>
                  {renderActions(model)}
                </Table.Cell>
              )}
            </Table.Row>
          ))}
          {robotoRunsUtility && (
            <Table.Row aria-label={ROBOTO_UTILITY_LABEL}>
              <Table.Cell />
              <Table.Cell>
                <span className={styles.modelCell}>
                  <span className={styles.modelLabel}>
                    {ROBOTO_UTILITY_LABEL}
                  </span>
                </span>
              </Table.Cell>
              <Table.Cell className={styles.nowrap}>Roboto</Table.Cell>
              <Table.Cell className={styles.modelId}>
                {robotoUtilityModelId}
              </Table.Cell>
              <Table.Cell>
                <span className={styles.badgeStack}>
                  <UtilityBadge />
                </span>
              </Table.Cell>
              <Table.Cell>
                <span className={styles.secondaryText}>Roboto-managed</span>
              </Table.Cell>
              {renderActions !== undefined && <Table.Cell />}
            </Table.Row>
          )}
        </Table.Body>
      </Table>
    </div>
  );
}

/** A model's role badges; `showUtility` off, a stored utility mark is not shown. */
function ModelRoles({
  model,
  showUtility,
}: {
  model: OrgLlmModel;
  showUtility: boolean;
}) {
  return (
    <span className={styles.badgeStack}>
      {model.is_default && <Badge variant="info">Default</Badge>}
      {showUtility && model.is_utility === true && <UtilityBadge />}
      {model.in_picker === false && (
        <Badge variant="secondary">Not in chat picker</Badge>
      )}
    </span>
  );
}

/** The Utility badge, with a tooltip saying what the utility model does. */
function UtilityBadge() {
  return (
    <Tooltip>
      <Tooltip.Trigger
        delay={250}
        render={<span tabIndex={0} className={styles.badgeTrigger} />}
      >
        <Badge variant="secondary">Utility</Badge>
      </Tooltip.Trigger>
      <Tooltip.Content>{UTILITY_ROLE}</Tooltip.Content>
    </Tooltip>
  );
}

/**
 * A model's Enabled switch: a checkbox for an admin, except on the default,
 * which cannot be switched off and shows a check mark titled with the reason
 * (a disabled checkbox there would read as off). Members get check and dash
 * marks: a disabled checkbox is too faint to read. While its own save is in
 * flight, a spinner stands in for the checkbox.
 */
function EnabledSwitch({
  model,
  control,
}: {
  model: OrgLlmModel;
  control: ModelEnabledControl | undefined;
}) {
  const label = `Enabled: ${model.label}`;
  if (control === undefined) {
    return isEnabled(model) ? (
      <Check role="img" aria-label={label} className={styles.enabledMark} />
    ) : (
      <Minus
        role="img"
        aria-label={`Off: ${model.label}`}
        className={classNames(styles.enabledMark, styles.offMark)}
      />
    );
  }
  if (model.is_default) {
    return (
      // A span carries the reason as a title: lucide's icon props have no
      // `title`, and the tooltip doubles as the mark's accessible description.
      <span
        role="img"
        aria-label={label}
        title={DEFAULT_STAYS_ON}
        className={styles.enabledMark}
      >
        <Check aria-hidden className={styles.enabledMark} />
      </span>
    );
  }
  if (control.pendingId === model.id) {
    return (
      <span className={styles.switchSlot}>
        <Spinner aria-label={`Saving: ${model.label}`} />
      </span>
    );
  }
  return (
    <Checkbox
      aria-label={label}
      checked={isEnabled(model)}
      // Not `disabled` while busy: the switch keeps its look and just ignores
      // the click, which the table's `aria-busy` explains.
      onCheckedChange={(enabled) => {
        if (!control.busy) {
          control.onToggle(model, enabled);
        }
      }}
    />
  );
}
